import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import dataService from 'services/dataService';
import {
  apiPatientAdminPanelSessionEnd,
  apiPatientAdminPanelSessionPing,
  apiPatientAdminPanelSessionStart,
} from 'services/dataService/resources';

const PING_INTERVAL = 10000;
const REFRESH_FLAG_KEY = 'patientAdminPanelRefresh';

const startAdminPanelSession = async ({
  patientGuid,
}: {
  patientGuid: string;
}) => {
  const { data, error } = await dataService.createOne(
    apiPatientAdminPanelSessionStart(),
    { patientGuid },
  );
  if (error) throw new Error(error);
  return data;
};

const pingAdminPanelSession = async ({
  patientGuid,
  isRefresh,
}: {
  patientGuid: string;
  isRefresh?: boolean;
}) => {
  const { data, error } = await dataService.createOne(
    apiPatientAdminPanelSessionPing(),
    { patientGuid, ...(isRefresh && { isRefresh: true }) },
  );
  if (error) {
    console.error('Admin panel ping failed:', error);
    return null;
  }
  return data;
};

const sendEndAdminPanelSession = (
  sessionId: string | null,
  reasonEnded: string,
  clientSentAt?: number,
) => {
  if (sessionId) {
    navigator.sendBeacon(
      apiPatientAdminPanelSessionEnd(),
      JSON.stringify({
        sessionId,
        reasonEnded,
        ...(clientSentAt !== undefined && {
          clientSentAt: new Date(clientSentAt).toISOString(),
        }),
      }),
    );
  }
};

const sendEndAdminPanelSessionAsync = (
  sessionId: string | null,
  reasonEnded: string,
): Promise<void> => {
  if (!sessionId) return Promise.resolve();
  return fetch(apiPatientAdminPanelSessionEnd(), {
    method: 'POST',
    keepalive: true,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId, reasonEnded }),
  }).then(() => undefined);
};

interface UsePatientAdminPanelSessionProps {
  patientGuid: string;
  canRun: boolean;
}

export const usePatientAdminPanelSession = ({
  patientGuid,
  canRun,
}: UsePatientAdminPanelSessionProps) => {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [serverElapsedTime, setServerElapsedTime] = useState<number>(0);
  const [blocked, setBlocked] = useState(false);
  const [activePatientGuid, setActivePatientGuid] = useState<string | null>(
    null,
  );

  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sessionRef = useRef<string | null>(null);
  const blockedRef = useRef(false);
  const sessionEndedRef = useRef(false);
  const startPingingRef = useRef<(() => void) | null>(null);
  const handlePingResultRef = useRef<((result: any) => void) | null>(null);

  useEffect(() => {
    sessionRef.current = sessionId;
  }, [sessionId]);

  const handlePingResult = (result: any) => {
    if (!result?.sessionId) return;
    setSessionId(result.sessionId);
    setServerElapsedTime(result.elapsedTime);
  };

  useEffect(() => {
    handlePingResultRef.current = handlePingResult;
  });

  const stopPinging = () => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
  };

  const endSession = (): Promise<void> => {
    if (sessionEndedRef.current) return Promise.resolve();
    sessionEndedRef.current = true;
    stopPinging();
    return sendEndAdminPanelSessionAsync(sessionRef.current, 'chart-close');
  };

  const { mutate } = useMutation({
    mutationFn: startAdminPanelSession,
    onSuccess(data) {
      if (data.blocked) {
        blockedRef.current = true;
        setBlocked(true);
        setActivePatientGuid(data.activePatientGuid);
        return;
      }
      setSessionId(data.sessionId);
      pingAdminPanelSession({ patientGuid }).then((r) =>
        handlePingResultRef.current?.(r),
      );
      startPingingRef.current?.();
    },
  });

  useEffect(() => {
    if (!patientGuid || !canRun) return;

    const startPinging = () => {
      if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = setInterval(async () => {
        const result = await pingAdminPanelSession({ patientGuid });
        handlePingResultRef.current?.(result);
      }, PING_INTERVAL);
    };

    startPingingRef.current = startPinging;

    let isPageHide = false;
    const handlePageHide = () => {
      isPageHide = true;
      if (blockedRef.current) return;
      if (sessionRef.current) {
        sessionStorage.setItem(REFRESH_FLAG_KEY, patientGuid);
      }
      sendEndAdminPanelSession(sessionRef.current, 'tab-close', Date.now());
    };

    window.addEventListener('pagehide', handlePageHide);

    const refreshPatientGuid = sessionStorage.getItem(REFRESH_FLAG_KEY);
    const isRefresh = refreshPatientGuid === patientGuid;
    sessionStorage.removeItem(REFRESH_FLAG_KEY);

    if (isRefresh) {
      startPinging();
      pingAdminPanelSession({ patientGuid, isRefresh: true }).then((r) =>
        handlePingResultRef.current?.(r),
      );
    } else {
      mutate({ patientGuid });
    }

    return () => {
      stopPinging();
      if (!isPageHide && !sessionEndedRef.current) {
        sendEndAdminPanelSession(sessionRef.current, 'chart-close');
      }
      sessionEndedRef.current = false;
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [patientGuid, canRun]);

  return {
    sessionId,
    serverElapsedTime,
    blocked,
    activePatientGuid,
    endSession,
  };
};
